import { DOCUMENT_TYPE_ATR } from '@/constants'
import CustomModal, { ModalBody, ModalFooter, ModalHeader } from '../shared/CustomModal'
import { useEffect, useMemo, useState } from 'react'
import { FaGoogleDrive, FaPlus, FaTrash } from 'react-icons/fa'
import * as actions from '@actions'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import ScrollableDocumentViewer from '../collection/DocumentViewerV3/ScrollableDocumentViewer'
import cn from 'clsx'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdOutlineWarning } from 'react-icons/md'


const DocumentExtractModal = ({
    
    documentExtractModal,
    folderId,
    transactionId,
    selectedPktId,
    
}) => {
    
    const [, setSearchParams] = useSearchParams()
    const apiConfig = useWireValue(store.apiConfig)
    
    const DEFAULT_EXTRACTDOCUMENT = {
        startPage: '', endPage: '', book: '', page: '', instrumentNumber: '', documentType: '',
    }
    const [extractDocument, setExtractDocument] = useState([DEFAULT_EXTRACTDOCUMENT])
    const [pdfUrl, setPdfUrl] = useState('')
    const [edErrors, setEdErrors] = useState([])
    const [idxErrors, setIdxErrors] = useState([])
    const [selectedPkt, setSelectedPkt] = useState(null)
    const [isUpdating, setIsUpdating] = useState(false)
    
    const isLocked = useMemo(() => {
        return selectedPkt?.lockedById && selectedPkt?.lockedById !== store.user.getValue().id
    }, [selectedPkt])
    
    const documentEditShowExtraFields = useMemo(() => !!apiConfig?.remoteConfig?.atr?.documentEditShowExtraFields,
        [apiConfig])
    
    const handleClose = async () => {
        
        await actions.unlockPacket(selectedPktId)
        
        setSearchParams(prev => {
            prev.delete('modal')
            prev.delete('id')
            prev.delete('folderId')
            prev.delete('selectedPktId')
            return prev
        })
        
        setSelectedPkt(null)
        
    }
    
    const addNewLine = index => {
        setExtractDocument(prevRows => {
            const updatedRows = [...prevRows]
            
            updatedRows.splice(index + 1, 0, DEFAULT_EXTRACTDOCUMENT)
            return updatedRows
        })
    }
    
    const handleExtractDocumentValue = (key, idx, value) => {
        setExtractDocument(extractDocument.map((doc, i) =>
            i === idx ? { ...doc, [key]: value } : doc,
        ))
    }
    
    const updateExtractDocument = async () => {
        
        if (isLocked) return
        
        try {
            
            setIsUpdating(true)
            await actions.updateExtractDocument(selectedPktId, extractDocument)
            toast.success(`${transactionId} information updated successfully`)
            await handleClose()
            
        } catch (error) {
            
            toast.error(`${transactionId} information update failed`)
            
        } finally {
            
            setIsUpdating(false)
            
        }
    }
    
    const deleteExtract = async (exDocId, idx) => {
        
        if (isLocked) return
        
        if (exDocId) {
            await actions.deleteExtract(exDocId)
            setExtractDocument(extractDocument.filter(def => def.id !== exDocId))
        } else {
            setExtractDocument(extractDocument.filter((_, i) => i !== idx))
        }
    }
    
    useEffect(() => {
        actions.findExtractDocument(selectedPktId).then(it => {
            setExtractDocument(it.length ? it : [DEFAULT_EXTRACTDOCUMENT])
        })
        
        actions.getAtrPacket(selectedPktId).then(it => {
            setSelectedPkt(it)
        })
        
    }, [])
    
    useEffect(() => {
        
        if (!selectedPkt) return
        
        const errors = []
        const indexes = []
        
        extractDocument.forEach((ed, index) => {
            
            if ((extractDocument[index + 1]?.startPage - ed.endPage) > 1) {
                errors.push({
                    error: 'Gap',
                    message: `There is a gap between lines ${index + 1} and ${index + 2}`,
                })
                indexes.push(index)
                indexes.push(index + 1)
            }
        })
        
        extractDocument.forEach((ed, index) => {
            
            if (ed.endPage >= extractDocument[index + 1]?.startPage) {
                errors.push({
                    error: 'Overlap',
                    message: `There is an overlap between lines ${index + 1} and ${index + 2}`,
                })
                indexes.push(index)
                indexes.push(index + 1)
            }
        })
        
        if (parseInt(extractDocument[0].startPage, 10) !== 1) {
            errors.push({
                error: 'wrongStartPage',
                message: 'The document has to start on page one',
            })
            indexes.push(0)
        }
        
        if (extractDocument[extractDocument.length - 1].endPage > selectedPkt.numPages) {
            errors.push({
                error: 'wrongEndPage',
                message: 'The page cannot be bigger than the number of pages of the document',
            })
            indexes.push(extractDocument.length - 1)
            
        }
        
        extractDocument.forEach((it, idx) => {
            if (parseInt(it.startPage, 10) > parseInt(it.endPage, 10)) {
                errors.push({
                    error: 'startBiggerThanEnd',
                    message: `Start page can't be bigger than end page at line ${idx + 1}`,
                })
                indexes.push(idx)
            }
        })
        
        extractDocument.forEach((it, idx) => {
            if (!it.documentType) {
                errors.push({
                    index: idx,
                    error: 'missingDocumentType',
                    message: `Document type is missing at line ${idx + 1}`,
                })
                indexes.push(idx)
            }
        })
        
        setEdErrors(errors)
        setIdxErrors(indexes)
        
    }, [selectedPkt, extractDocument])
    
    
    useEffect(() => {
        
        if (!selectedPkt) return
        
        actions.findAtrDocument(transactionId, selectedPkt.packetName)
            .then(blob => {
                const url = URL.createObjectURL(blob)
                
                setPdfUrl(url)
            }).catch(error => console.error('Error to fetch PDF:', error))
    }, [selectedPkt])
    
    return (
        <CustomModal
            open={documentExtractModal}
            modalHandler={handleClose}
            className="max-w-[95vw]">
            <ModalHeader>
                {transactionId}
            </ModalHeader>
            <ModalBody>
                {isLocked &&
                    <div role="alert" className="alert alert-warning mb-4">
                        <MdOutlineWarning />
                        <span>
                            Document Locked! This document is already being edited by:{' '}
                            <span className="font-semibold">
                                {selectedPkt?.userLock?.firstName} {selectedPkt?.userLock?.lastName}
                            </span>
                        </span>
                    </div>
                }
                <div className={cn('flex gap-4', {
                    'h-[calc(100vh-200px)]': !isLocked,
                    'h-[calc(100vh-274px)]': isLocked,
                })}>
                    <div className="flex flex-col flex-1 gap-2">
                        <ScrollableDocumentViewer url={pdfUrl} />
                    </div>
                    <div className={cn('flex-1 overflow-auto', {
                        'h-[calc(100vh-200px)]': !isLocked,
                        'h-[calc(100vh-274px)]': isLocked,
                    })}>
                        
                        {edErrors && (
                            <div className="collapse collapse-arrow bg-base-100 border-base-300 rounded">
                                <input type="checkbox" className="w-full min-h-[2.5rem]" />
                                <div className="collapse-title font-semibold min-h-[2.5rem] py-2 after:!top-[1.3rem]">
                                    <span className={cn('badge mr-3', edErrors.length !== 0
                                        ? 'badge-error' : 'badge-success')}>
                                        {edErrors.length}
                                    </span>
                                    {edErrors.length === 1 ? 'Error' : 'Errors'}
                                </div>
                                <div className="collapse-content text-sm">
                                    <div className="max-h-[100px] overflow-auto">
                                        <ul>
                                            {edErrors.map(e => {
                                                return <li> {e.message} </li>
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="flex justify-between">
                            <a className="btn btn-link !p-0"
                                target="_blank"
                                href={`https://drive.google.com/drive/folders/${folderId}`}>
                                <FaGoogleDrive />{transactionId}
                            </a>
                            <button className="btn btn-link" onClick={() => addNewLine()}>
                                Add New Line <FaPlus />
                            </button>
                        </div>
                        <table className="table table-xs">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th className="border"> Start Page </th>
                                    <th className="border"> End Page </th>
                                    {documentEditShowExtraFields && (<th className="border "> Book Volume</th>)}
                                    {documentEditShowExtraFields && (<th className="border "> Book Page </th>)}
                                    {documentEditShowExtraFields && (<th className="border "> Instrument Number </th>)}
                                    <th className="border"> Document Type </th>
                                </tr>
                            </thead>
                            <tbody>
                                {extractDocument.map((exDoc, idx) => {
                                    return (
                                        <tr key={exDoc?.id || idx}>
                                            <td className="text-base">{idx + 1}</td>
                                            <td className="w-1/12">
                                                <input
                                                    type="text"
                                                    disabled={isLocked}
                                                    className={cn(`input input-bordered w-full h-[2.5rem]
                                                        px-0 text-center`, {
                                                        'border-red-400': idxErrors.some(e => e === idx),
                                                    })}
                                                    value={exDoc.startPage}
                                                    onChange={e => {
                                                        const parsed = parseInt(e.target.value, 10)
                                                        
                                                        if (e.target.value !== '' && (isNaN(parsed) || parsed < 1))
                                                            return
                                                        handleExtractDocumentValue('startPage', idx, parsed || '')
                                                    }
                                                    } />
                                            </td>
                                            <td className="w-1/12">
                                                <input
                                                    type="text"
                                                    disabled={isLocked}
                                                    className={cn(`input input-bordered w-full h-[2.5rem]
                                                        px-0 text-center`, {
                                                        'border-red-400': idxErrors.some(e => e === idx),
                                                    })}
                                                    value={exDoc.endPage}
                                                    onChange={e => {
                                                        const parsed = parseInt(e.target.value, 10)
                                                        
                                                        if (e.target.value !== '' && (isNaN(parsed) || parsed < 1))
                                                            return
                                                        handleExtractDocumentValue('endPage', idx, parsed || '')
                                                    }
                                                    } />
                                            </td>
                                            {documentEditShowExtraFields && (
                                                <td className="w-1/12">
                                                    <input
                                                        disabled={isLocked}
                                                        type="text"
                                                        className="input input-bordered w-full px-0 text-center"
                                                        value={exDoc.bookVolume}
                                                        onChange={e =>
                                                            handleExtractDocumentValue('bookVolume',
                                                                idx, e.target.value)} />
                                                </td>
                                            )}
                                            
                                            {documentEditShowExtraFields && (
                                                <td className="w-1/12">
                                                    <input
                                                        type="text"
                                                        disabled={isLocked}
                                                        className="input input-bordered w-full px-0 text-center"
                                                        value={exDoc.bookPage}
                                                        onChange={e =>
                                                            handleExtractDocumentValue('bookPage',
                                                                idx, e.target.value)} />
                                                </td>
                                            )}
                                            
                                            {documentEditShowExtraFields && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        disabled={isLocked}
                                                        className="input input-bordered w-full px-2"
                                                        value={exDoc.instrumentNumber}
                                                        onChange={e =>
                                                            handleExtractDocumentValue('instrumentNumber',
                                                                idx, e.target.value)} />
                                                </td>
                                            )}
                                            <td>
                                                <select
                                                    className={cn('select select-bordered py-1 w-full min-h-[2.5rem]', {
                                                        'border-red-400': !exDoc.documentType
                                                            && idxErrors.some(e => e === idx),
                                                    })}
                                                    disabled={isLocked}
                                                    value={exDoc.documentType}
                                                    onChange={e => handleExtractDocumentValue('documentType', idx,
                                                        e.target.value)}>
                                                    <option></option>
                                                    {DOCUMENT_TYPE_ATR.map(dt =>
                                                        <option key={dt} value={dt}>
                                                            {dt}
                                                        </option>)}
                                                </select>
                                            </td>
                                            {!isLocked && <td className="flex gap-6 h-[48px]">
                                                <button className="cursor-pointer tooltip tooltip-bottom-left"
                                                    data-tip="Add new line below"
                                                    onClick={() => addNewLine(idx)}>
                                                    <FaPlus className="hover:opacity-50 transition" />
                                                </button>
                                                <button
                                                    onClick={() => deleteExtract(exDoc.id, idx)}
                                                    className="cursor-pointer text-red-400 hover:text-red-600">
                                                    <FaTrash />
                                                </button>
                                            </td>}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            
            </ModalBody>
            <ModalFooter>
                <button className="btn btn-outline btn-primary" onClick={handleClose}>Cancel</button>
                <button
                    className="btn btn-primary"
                    disabled={isLocked || extractDocument.some(it => !it.startPage || !it.endPage || !it.documentType)
                        || edErrors.length !== 0 || isUpdating}
                    onClick={updateExtractDocument}>
                    {isUpdating ? 'Updating...' : 'Update'}
                </button>
            </ModalFooter>
        </CustomModal>
    )
}

export default DocumentExtractModal
